import { useState, useEffect } from 'react';
import {
	Typography, Box, Button, Modal, Paper, Stepper, Step, StepLabel,
	StepContent, CircularProgress, Fade, Slide, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Download as DownloadIcon, ArrowForward as ArrowForwardIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCSV, fetchWrapperNoAuth, SERVER } from '../../utils/helpers';

const RestoreBatchTutorial = ({ open, onClose, batchName }) => {
	const { theme } = useTheme();
	const { user, password } = useAuth();

	const navigate = useNavigate();

	const [loadingBatchData, setLoadingBatchData] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [batchProducts, setBatchProducts] = useState([]);

	const styles = {
		tutorialModal: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)',
			width: '45vw',
			maxHeight: '80vh',
			bgcolor: theme.background.secondary,
			borderRadius: 2,
			border: `4px solid ${theme.color.primary}`,
			boxShadow: 24,
			p: 4,
			outline: 'none',
			overflow: 'auto',
			display: 'flex',
			flexDirection: 'column',
		},
		tutorialHeader: {
			fontFamily: theme.fontFamily,
			color: theme.text.primary,
			marginBottom: 3,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		loadingContainer: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			padding: 4,
			minHeight: '200px',
		},
		loadingText: {
			marginTop: 2,
			color: theme.text.secondary,
			fontFamily: theme.fontFamily,
		},
		stepLabel: {
			'& .MuiStepLabel-label': {
				fontFamily: theme.fontFamily,
				color: theme.text.primary,
				fontSize: '1.1rem',
				fontWeight: 500,
			},
			'& .MuiStepLabel-label.Mui-active': {
				color: theme.color.primary,
			},
			'& .MuiStepLabel-label.Mui-completed': {
				color: theme.accent.success,
			},
		},
		stepContent: {
			'& .MuiStepContent-root': {
				borderColor: theme.border.medium,
			},
		},
		stepDescription: {
			fontFamily: theme.fontFamily,
			color: theme.text.secondary,
			marginBottom: 2,
			lineHeight: 1.6,
		},
		stepAction: {
			backgroundColor: theme.color.primary,
			color: theme.text.negative,
			fontFamily: theme.fontFamily,
			textTransform: 'none',
			borderRadius: '8px',
			padding: '8px 20px',
			'&:hover': {
				backgroundColor: theme.color.secondary,
			},
			display: 'flex',
			alignItems: 'center',
			gap: 1,
			marginRight: 1,
		},
		secondaryButton: {
			backgroundColor: 'transparent',
			color: theme.text.secondary,
			fontFamily: theme.fontFamily,
			textTransform: 'none',
			borderRadius: '8px',
			padding: '8px 20px',
			border: `1px solid ${theme.border.medium}`,
			'&:hover': {
				backgroundColor: `${theme.color.primary}10`,
				borderColor: theme.color.primary,
			},
		},
		tutorialBatchName: {
			backgroundColor: `${theme.color.primary}20`,
			padding: '8px 16px',
			borderRadius: '8px',
			fontFamily: theme.fontFamily,
			color: theme.color.primary,
			fontWeight: 'bold',
			display: 'inline-block',
			marginBottom: 2,
		},
		closeButton: {
			color: theme.text.secondary,
			'&:hover': {
				backgroundColor: `${theme.accent.error}20`,
				color: theme.accent.error,
			},
		},
		instructionText: {
			fontFamily: theme.fontFamily,
			color: theme.text.primary,
			fontSize: '0.95rem',
			lineHeight: 1.6,
			backgroundColor: `${theme.color.primary}08`,
			padding: '12px 16px',
			borderRadius: '8px',
			marginTop: 1,
			marginBottom: 2,
			border: `1px solid ${theme.color.primary}30`,
		},
	};

	const tutorialSteps = [
		{
			label: 'Download Batch Recovery File',
			description: 'Download the CSV file containing your batch data. This file contains all the product information needed to restore your batch.',
			action: 'Download .csv File',
			icon: <DownloadIcon />,
		},
		{
			label: 'Upload The File',
			description: 'Use the downloaded CSV file to restore your batch.',
			action: 'Navigate to CSV Upload',
			icon: <ArrowForwardIcon />,
		},
	];

	useEffect(() => {
		if (open && batchName) {
			loadBatchData();
		}
	}, [open, batchName]);

	const loadBatchData = async () => {
		setLoadingBatchData(true);

		const body = {
			batch: batchName
		}

		try {
			const data = await fetchWrapperNoAuth(`${SERVER}/falcon_get_metadata_by_batch/${user}`, user, password, { method: "POST", body });
			setBatchProducts(data);
		} catch (err) {
			console.error("Error fetching batch products", err);
			setBatchProducts([]);
		} finally {
			setLoadingBatchData(false);
		}
	};

	const handleDownloadBatch = () => {
		if (!batchProducts || batchProducts.length <= 0) {
			return;
		}

		const headers = ['Barcode', 'Cost Price', 'Name', 'Image', 'Source', 'SKU', 'Expiration Date', 'Purchase Date', 'Quantity', 'Notes'];
		const objects = batchProducts.map((product) => {
			return {
				'Barcode': product.upc ?? "",
				'Cost Price': product.cost ?? 0,
				'Name': product.product_name ?? "",
				'Image': product.image ?? "",
				'Source': product.source ?? "",
				'SKU': product.sku ?? "",
				'Expiration Date': product.purchase_date ?? "",
				'Purchase Date': product.purchase_date ?? "",
				'Quantity': product.qty ?? 0,
				'Notes': product.note ?? ""
			}
		});

		exportToCSV(headers, objects, `restore_${batchName}`);
		handleNextStep();
	};

	const handleNextStep = () => {
		if (activeStep < tutorialSteps.length - 1) {
			setActiveStep(activeStep + 1);
		}
	};

	const handleCloseTutorial = () => {
		setActiveStep(0);
		setBatchProducts([]);
		onClose();
	};

	const handleCompleteRestore = () => {
		handleCloseTutorial();
	};

	return (
		<Modal open={open} onClose={handleCloseTutorial}>
			<Fade in={open}>
				<Paper sx={styles.tutorialModal}>
					<Box sx={styles.tutorialHeader}>
						<Typography variant="h5" component="h2" sx={{ fontFamily: theme.fontFamily}}>
							Restore Batch: <span style={styles.tutorialBatchName}>{batchName}</span>
						</Typography>
						<IconButton sx={styles.closeButton} onClick={handleCloseTutorial}>
							<CloseIcon />
						</IconButton>
					</Box>

					{loadingBatchData ? (
						<Box sx={styles.loadingContainer}>
							<CircularProgress size={50} sx={{ color: theme.color.primary }} />
							<Typography sx={styles.loadingText}>
								Loading batch data...
							</Typography>
						</Box>
					) : (
						<Slide direction="up" in={!loadingBatchData} mountOnEnter unmountOnExit>
							<Box>
								<Stepper activeStep={activeStep} orientation="vertical" sx={styles.stepContent}>
									{tutorialSteps.map((step, index) => (
										<Step key={step.label}>
											<StepLabel sx={styles.stepLabel}>
												{step.label}
											</StepLabel>
											<StepContent>
												<Typography sx={styles.stepDescription}>
													{step.description}
												</Typography>

												{index === 1 && (
													<Box sx={styles.instructionText}>
														<Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 1, fontFamily: theme.fontFamily }}>
															How to upload the file:
														</Typography>
														<Typography variant="body2" sx={{ marginBottom: 0.5, fontFamily: theme.fontFamily }}>
															1. Go to the sidebar and click on <strong>"Upload From CSV"</strong>
														</Typography>
														<Typography variant="body2" sx={{ marginBottom: 0.5, fontFamily: theme.fontFamily }}>
															2. Select <strong>"UPC"</strong> as your identifier type
														</Typography>
														<Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
															3. Upload the downloaded CSV file to restore your batch
														</Typography>
													</Box>
												)}

												<Box sx={{ mt: 2, mb: 1, display: "flex" }}>
													{index === 0 && (
														<Button
															variant="contained"
															onClick={handleDownloadBatch}
															sx={styles.stepAction}
															startIcon={step.icon}
															disabled={batchProducts.length < 1}
														>
															{step.action}
														</Button>
													)}
													{index === 1 && (
														<Button
															variant="contained"
															onClick={() => navigate("/csv-upload")}
															sx={styles.stepAction}
															startIcon={step.icon}
														>
															{step.action}
														</Button>
													)}
													{activeStep > 0 && (
														<Button
															sx={styles.secondaryButton}
															onClick={handleCompleteRestore}
														>
															Close Tutorial
														</Button>
													)}
												</Box>
											</StepContent>
										</Step>
									))}
								</Stepper>
							</Box>
						</Slide>
					)}
				</Paper>
			</Fade>
		</Modal>
	);
};

export default RestoreBatchTutorial;